import React from "react";
import { getAllCombinationsOfCount, getCountryData, getGroup, getIndexOfMax, getPrediction, getPredictionSeats } from "../calculations/utilsV2";
import { getColorByLikelihood, getCurrentGovernment, getTextByLikelihood, styleByGroup } from "../calculations/calcV2";

interface QuestionBoardProps {
    countryStat: CountryStat,
}

export const QuestionBoardV2 = (props: QuestionBoardProps) => {
    const { countryStat } = props;

    const CONFIDENCE = 2;
    
    const prediction = getPrediction(countryStat);
    if (!prediction) { return <div /> }

    const getYesNo = (cond: boolean) => cond ? { group: 'G', party: 'yes' } : { group: 'S&D', party: 'no' };
    const getLessMore = (cond: boolean) => cond ? { group: 'G', party: 'less' } : { group: 'S&D', party: 'more' };
    const getLikelihood = (a: number, b: number) => 12 * (a-b)*(a-b) + 40;
    // Probability that one probabilistic number is the biggest
    const getMaxLikelihood = (arr: number[]) => {
        var max = Math.max(...arr);
        return 90 / (arr.reduce((s, a) => s + Math.max(0, (a - max + CONFIDENCE*2)/(CONFIDENCE*2)), 0));
    }
    // Probability that specific amount of probabilistic numbers are over a limit
    const getLikelihoodXPassesThreshold = () => {
        const probabilities = prediction.filter(x => Math.abs(x.value - threshold) < CONFIDENCE).map((a) => (a.value - threshold)/CONFIDENCE, 0)
            .map(a => (a < 0 ? 1 : 0) + Math.sign(a) * 1 / (2 - Math.abs(a)));
        let result = 0;
        const thresholdValues = prediction.filter(x => Math.abs(x.value - threshold) < CONFIDENCE);
        const combinations = getAllCombinationsOfCount(thresholdValues.length, thresholdValues.reduce((s, a) => s + (a.value > threshold ? 1 : 0), 0));
        for (var item of combinations){
            let onePossiblity = 1;
            for (var i = 0; i < probabilities.length; ++i) {
                onePossiblity *= item.includes(i) ? probabilities[i] : 1 - probabilities[i];
            }
            result += onePossiblity;
        }
        return 100 * result;
    }

    const predictionSeats = getPredictionSeats(countryStat, prediction);
    const indexOfMax = getIndexOfMax(prediction.map(x => x.value));
    //const parties = getParties(countryStat);
    const countryData = getCountryData(countryStat);
    const parliamentSeats = countryData.ParliamentSeats ?? 0;
    const threshold = countryData.ParliamentThreshold ?? 0;
    const govParties = getCurrentGovernment(countryStat).Parties;
    const govMajority = predictionSeats.reduce((s, a) => s + (govParties.includes(a.party) ? a.value : 0), 0);

    const governmentMajorityYn = getYesNo(govMajority >= parliamentSeats/2 + 1);
    const pmParty = { party: govParties[0], group: getGroup(countryStat, govParties[0]) };
    const pmPartyPrev = countryStat.Elections[0].Values[countryStat.Parties.indexOf(pmParty.party)];
    const pmPartyPred = prediction.find(x => x.party === pmParty.party)?.value ?? 0;

    return (<div>
        <span className="underline">election predictions</span>
        <div className="bigMargin">
            <QuestionV2 text="Which party will be the biggest?" answer={prediction[indexOfMax]} likelihood={getMaxLikelihood(prediction.map(x => x.value))} />
            <QuestionV2 text="How many parties will get into the parliament?" answerCount={prediction.reduce((s, a) => s + (a.value > threshold ? 1 : 0), 0)} likelihood={getLikelihoodXPassesThreshold()} />
            <QuestionV2 text="Will the current government gain majority?" answer={governmentMajorityYn} likelihood={getLikelihood((govMajority - parliamentSeats/2 + 1)/(CONFIDENCE * parliamentSeats / 80), 0)} />
            <QuestionV2 answer={getLessMore(pmPartyPrev - 5 < pmPartyPred)} likelihood={getLikelihood(pmPartyPred, pmPartyPrev-5)}>
                Will <span className={"highlight " + styleByGroup(pmParty?.group)}>{pmParty?.party}</span> lose more or less than 5%?
            </QuestionV2>
        </div>
    </div>)
}

interface QuestionProps {
    text?: string,
    answer?: { group: string, party: string },
    answerCount?: number,
    likelihood: number,
    children?: any,
}

export const QuestionV2 = (props: QuestionProps) => {
    const { text, answer, answerCount, likelihood, children } = props;

    return (<div className="margin8">
        {text ?? children} <span className={"highlight " + styleByGroup(answer?.group ?? 'NI')}>{answer?.party ?? answerCount ?? ''}</span> 
        <span className={"highlight"} style={{backgroundColor: getColorByLikelihood(likelihood)}}>{getTextByLikelihood(likelihood)}</span>
    </div>)
}